import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Typography } from "@mui/material";
import { InlineWidget } from "react-calendly";
import { useStudents } from "../../../../../context/students";
import axiosManager from "../../../../../services/axiosManager";
import useStatuses from "../../../../../context/status/useStatuses";
import SnackbarToast from "../../../../../components/SnackbarToast";
import Preloader from "../../../../../components/Preloader";
import { useTranslate } from "../../../../../hooks/useTranslate";
import { KEY_PROGRESS } from "../../../../../utils/getVerificationStatusKey";
import { isVersionUS } from "../../../../../services/versionService";
import {
    CALENDLY_URL_IB,
    CALENDLY_URL_SEN,
    CALENDLY_URL_US,
    CALENDLY_URL_YEAR_11,
    CALENDLY_URL_YEAR_13,
} from "../../config";

const CalendlyWidget = () => {
    const { t } = useTranslate();
    const { activeStudentId } = useStudents();
    const [isCallScheduled, setIsCallScheduled] = useState(false);
    const { statuses: { consultationCall }, refetchGeneralStatuses, generalStatuses } = useStatuses();

    const {
        isFetching: isLoadingParent, data: user,
    } = useQuery({
        queryKey: ["parent/contacts"],
        queryFn: () => axiosManager.get$("parent/api/Contacts").then((response) => response?.data),
        retry: 0,
    });

    const {
        studyingIb, hasSafeguardingNeed, yearGroup = 0,
    } = generalStatuses?.student || {};

    const {
        mutate: setCallAsScheduled, reset: resetSetCallAsScheduled,
        isPending: isSettintCall, isSuccess: isSuccessSetCall, error: errorSetCall,
    } = useMutation({
        mutationFn: () => axiosManager.patch$(`/parent/api/Students/${activeStudentId}/SetCallAsScheduled`, {}),
        onSuccess: () => {
            refetchGeneralStatuses();
        },
    });

    const calendlyUrl = useMemo(() => {
        if (isVersionUS) return CALENDLY_URL_US;
        if (yearGroup === 11) return CALENDLY_URL_YEAR_11;
        if (yearGroup === 13) return CALENDLY_URL_YEAR_13;
        if (studyingIb) return CALENDLY_URL_IB;
        if (hasSafeguardingNeed) return CALENDLY_URL_SEN;

        return undefined;
    }, [yearGroup, studyingIb, hasSafeguardingNeed]);

    useEffect(() => {
        const handleCalendlyEvent = (event) => {
            if (event.data.event === "calendly.event_scheduled") {
                setIsCallScheduled(true);
                setCallAsScheduled();
            }
        };

        window.addEventListener("message", handleCalendlyEvent);

        return () => {
            window.removeEventListener("message", handleCalendlyEvent);
        };
    }, [setCallAsScheduled]);

    return (
        <>
            <Preloader isLoading={isSettintCall || isLoadingParent}>
                {(isCallScheduled || consultationCall.key === KEY_PROGRESS) ? null : (calendlyUrl && (
                    <InlineWidget
                        url={calendlyUrl}
                        style={{ minWidth: "320px", height: "700px" }}
                        prefill={{
                            name: `${user?.forename || ""} ${user?.surname || ""}`,
                            email: user?.emailAddress,
                        }}
                    />
                ))}
            </Preloader>
            <SnackbarToast
                open={isSuccessSetCall || !!errorSetCall}
                isSuccess={isSuccessSetCall}
                isError={errorSetCall}
                onClose={resetSetCallAsScheduled}
            >
                {isSuccessSetCall
                    ? t("enrolmentAgreement.successIssueAgreement")
                    : `${t("enrolmentAgreement.errorIssueAgreement")} (status ${errorSetCall?.response?.status})`}
            </SnackbarToast>
        </>
    );
};

export default CalendlyWidget;
